import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * @param {boolean} enabled - start/stop the capture on this toggle
 * @param {(error: Error) => void} onError
 */
export function useMedia(enabled, onError) {
  const [stream, setStream] = useState(/** @type {MediaStream|null} */ (null));
  const [facingMode, setFacingMode] = useState('user'); // 'user' | 'environment'
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  // Capture state lives outside the effect so it survives cleanups.
  const activeRef = useRef(/** @type {boolean} */ (false));
  const streamRef = useRef(stream);
  streamRef.current = stream;

  // Check for multiple cameras (front/back on mobile or multiple USB webcams)
  useEffect(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return;
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      const videoInputs = devices.filter((d) => d.kind === 'videoinput');
      setHasMultipleCameras(videoInputs.length > 1 || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!enabled) {
      activeRef.current = false;
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        setStream(null);
      }
      return;
    }

    if (activeRef.current) return;
    activeRef.current = true;

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const isSecure = typeof window !== 'undefined' && window.isSecureContext;
      const msg = !isSecure
        ? 'Браузер заблокировал доступ к медиа: страница открыта в небезопасном контексте.'
        : 'Браузер не поддерживает navigator.mediaDevices.getUserMedia.';
      console.warn('[media] ' + msg + ' Using synthetic stream fallback.');
      onError(new Error(msg + ' Включён тестовый видеопоток.'));
      const synthetic = createSyntheticMediaStream();
      setStream(synthetic);
      return;
    }

    navigator.mediaDevices
      .getUserMedia({
        video: { facingMode: { ideal: facingMode } },
        audio: true,
      })
      .then((s) => {
        setStream(s);
      })
      .catch(async (firstErr) => {
        if (firstErr.name === 'NotFoundError' || firstErr.name === 'NotReadableError') {
          try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevice = devices.find((d) => d.kind === 'videoinput');
            const audioDevice = devices.find((d) => d.kind === 'audioinput');
            const audio = audioDevice ? (audioDevice.deviceId ? { deviceId: { exact: audioDevice.deviceId } } : true) : false;
            const video = videoDevice ? (videoDevice.deviceId ? { deviceId: { exact: videoDevice.deviceId } } : true) : false;
            if (audio || video) {
              const s = await navigator.mediaDevices.getUserMedia({ audio, video });
              setStream(s);
              return;
            }
          } catch (retryErr) {
            firstErr = retryErr;
          }
        }
        console.warn('[media] real hardware capture failed, using test video stream fallback:', firstErr.message);
        onError(new Error(`Камера/микрофон недоступны (${firstErr.message}). Включён тестовый видеопоток.`));
        const synthetic = createSyntheticMediaStream();
        setStream(synthetic);
      });

    return () => {
      if (!enabled) activeRef.current = false;
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [enabled, onError]); // stream intentionally omitted

  // Switch between front and back camera (especially on mobile)
  const switchCamera = useCallback(async () => {
    const nextMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(nextMode);

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return;

    try {
      const newVideoStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: nextMode } },
      }).catch(() =>
        navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: nextMode } },
        })
      );

      const newVideoTrack = newVideoStream.getVideoTracks()[0];
      if (!newVideoTrack) return;

      const current = streamRef.current;
      if (current) {
        // Stop old video tracks
        current.getVideoTracks().forEach((t) => t.stop());
        // Combine audio and new video
        const updatedStream = new MediaStream([
          ...current.getAudioTracks(),
          newVideoTrack,
        ]);
        setStream(updatedStream);
      }
    } catch (err) {
      console.warn('[media] could not flip camera:', err);
    }
  }, [facingMode]);

  return {
    stream,
    facingMode,
    switchCamera,
    hasMultipleCameras,
  };
}

/**
 * Creates a synthetic MediaStream with an animated 25fps video canvas and a silent audio track.
 * Used when real hardware devices are not available or blocked, ensuring WebRTC call flow works.
 */
function createSyntheticMediaStream(label = 'Тестовое видео') {
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 360;
  const ctx = canvas.getContext('2d');

  let frame = 0;
  const draw = () => {
    frame++;
    const hue = (frame * 2) % 360;
    const grad = ctx.createLinearGradient(0, 0, 640, 360);
    grad.addColorStop(0, `hsl(${hue}, 50%, 20%)`);
    grad.addColorStop(1, `hsl(${(hue + 80) % 360}, 60%, 25%)`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 640, 360);

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 26px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(label, 320, 150);

    ctx.fillStyle = '#cbd5e1';
    ctx.font = '18px monospace';
    ctx.fillText(new Date().toLocaleTimeString(), 320, 195);

    const x = 320 + Math.cos(frame * 0.08) * 140;
    const y = 265 + Math.sin(frame * 0.08) * 35;
    ctx.beginPath();
    ctx.arc(x, y, 16, 0, Math.PI * 2);
    ctx.fillStyle = '#38bdf8';
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.stroke();
  };

  draw();
  const timer = setInterval(draw, 1000 / 25);
  const stream = canvas.captureStream ? canvas.captureStream(25) : new MediaStream();
  const videoTrack = stream.getVideoTracks()[0];
  if (videoTrack) {
    const origStop = videoTrack.stop.bind(videoTrack);
    videoTrack.stop = () => {
      clearInterval(timer);
      origStop();
    };
  }

  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      const actx = new AudioCtx();
      const dest = actx.createMediaStreamDestination();
      const osc = actx.createOscillator();
      const gain = actx.createGain();
      gain.gain.value = 0.0001;
      osc.connect(gain);
      gain.connect(dest);
      osc.start();
      const audioTrack = dest.stream.getAudioTracks()[0];
      if (audioTrack) {
        stream.addTrack(audioTrack);
        const origAudioStop = audioTrack.stop.bind(audioTrack);
        audioTrack.stop = () => {
          try { osc.stop(); actx.close(); } catch (_) {}
          origAudioStop();
        };
      }
    }
  } catch (_) {}

  return stream;
}
